import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import type { Aggregation } from '../../types/domain';
import {
  dataApiClient,
  type AgentAggregationDimension,
  type TripAggregationDimension,
} from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';

type AttributeAggregationDimension = Extract<
  AgentAggregationDimension | TripAggregationDimension,
  | 'gender'
  | 'age'
  | 'nationality'
  | 'end_activity_type'
  | 'longest_distance_mode'
>;

type UseAttributeAggregationData = {
  isLoading: boolean;
  aggregations: Map<string, number>;
};

const EMPTY_DATA = new Map<string, never>();

const useAttributeAggregationData = (
  dimension: AttributeAggregationDimension
): UseAttributeAggregationData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();

  // 棒グラフにはフィルターで除外せずに全要素を表示したいため条件から除外する
  const filter = { ...apiFilter, [dimension]: undefined };

  const { data, isFetching } = useQuery({
    queryKey: ['attributeAggregation', currentSimDataId, filter, dimension],
    queryFn: currentSimDataId
      ? async ({
          signal,
        }: QueryFunctionContext): Promise<Map<string, number>> => {
          switch (dimension) {
            case 'gender':
            case 'age':
            case 'nationality':
              return dataApiClient
                .getAgentAggr(
                  currentSimDataId,
                  filter,
                  'count',
                  dimension,
                  undefined,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
            case 'end_activity_type':
            case 'longest_distance_mode':
              return dataApiClient
                .getTripAggr(
                  currentSimDataId,
                  filter,
                  'count',
                  dimension,
                  undefined,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
          }
        }
      : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: 'others' },
  });

  return { isLoading: isFetching, aggregations: data ?? EMPTY_DATA };
};

export type { AttributeAggregationDimension };
export { useAttributeAggregationData };
